import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, ClipboardList, Sparkles, CheckCheck, Circle } from "lucide-react";

const POLL_MS = 60_000;

function humanize(iso) {
  if (!iso) return "";
  try {
    const now = new Date();
    const then = new Date(iso);
    const diff = (now - then) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 7 * 86400) return `${Math.floor(diff / 86400)}d ago`;
    return then.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  } catch { return ""; }
}

function typeIcon(type) {
  if (type === "advisor.action") return { Icon: ClipboardList, color: "text-amber-700 bg-amber-50 border-amber-200", label: "Action" };
  if (type === "advisor.recommendation") return { Icon: Sparkles, color: "text-cyan-700 bg-cyan-50 border-cyan-200", label: "Recommendation" };
  return { Icon: Bell, color: "text-slate-600 bg-slate-50 border-slate-200", label: "Notification" };
}

export default function NotificationsBell() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const mounted = useRef(true);

  const fetchNow = async () => {
    try {
      const { data } = await api.get("/notifications?limit=25");
      if (!mounted.current) return;
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {}
  };

  useEffect(() => {
    mounted.current = true;
    fetchNow();
    const t = setInterval(fetchNow, POLL_MS);
    return () => { mounted.current = false; clearInterval(t); };
  }, []);

  const markRead = async (n) => {
    if (!n.read_at) {
      try { await api.patch(`/notifications/${n.id}/read`); } catch {}
    }
    if (n.link) {
      setOpen(false);
      nav(n.link);
    }
    fetchNow();
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); } catch {}
    fetchNow();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative rounded-full h-9 w-9 flex items-center justify-center border border-slate-200 bg-white hover:bg-slate-50 transition-colors"
          data-testid="notifications-bell-trigger"
          aria-label={`Notifications${unread ? ` — ${unread} unread` : ""}`}
        >
          <Bell className="h-4 w-4 text-slate-700" />
          {unread > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-mono font-semibold flex items-center justify-center border-2 border-white"
              data-testid="notifications-bell-unread"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 overflow-hidden" data-testid="notifications-popover">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div>
            <div className="text-[10.5px] font-mono uppercase tracking-widest text-slate-500">Notifications</div>
            <div className="font-display font-semibold text-[13.5px] text-slate-900 mt-0.5">
              {unread ? `${unread} unread` : "You're all caught up"}
            </div>
          </div>
          {unread > 0 && (
            <button
              onClick={markAll}
              className="inline-flex items-center gap-1 text-[11.5px] text-cyan-700 hover:text-cyan-900 font-medium"
              data-testid="notifications-mark-all"
            >
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-8 text-center">
              <Bell className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <div className="text-[12.5px] text-slate-500">No notifications yet. When your advisor posts a new action or recommendation, it'll land here.</div>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="notifications-list">
              {items.map((n) => {
                const { Icon, color, label } = typeIcon(n.type);
                const isUnread = !n.read_at;
                return (
                  <li key={n.id}>
                    <button
                      onClick={() => markRead(n)}
                      className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex gap-3 ${isUnread ? "bg-cyan-50/30" : ""}`}
                      data-testid={`notification-${n.id.slice(0, 8)}`}
                    >
                      <div className={`h-8 w-8 rounded-md border flex items-center justify-center shrink-0 ${color}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
                          <div className="text-[10px] font-mono text-slate-400 shrink-0">{humanize(n.created_at)}</div>
                        </div>
                        <div className="text-[13px] font-medium text-slate-900 mt-0.5 leading-snug">{n.title}</div>
                        {n.body && <div className="text-[12px] text-slate-600 mt-0.5 leading-snug line-clamp-2">{n.body}</div>}
                        <div className="text-[10.5px] text-slate-500 mt-1 font-mono">by {n.actor_name || "your advisor"}</div>
                      </div>
                      {isUnread && <Circle className="h-2 w-2 mt-1 fill-cyan-500 text-cyan-500 shrink-0" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="border-t border-slate-100 bg-slate-50/40 px-4 py-2.5 text-[11px] text-slate-500 font-mono uppercase tracking-widest text-center">
          Refreshes every 60s
        </div>
      </PopoverContent>
    </Popover>
  );
}
